
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {Image, Steps, Invert, Notes} from 'mdx-deck'
import {
    CodeSurfer,
    CodeSurferColumns,
    Step,
} from "code-surfer";
import {github} from "@code-surfer/themes";
import suoraTheme from "./theme"
import {getLowercaseGraphQl, LowercaseGraphQl} from "./components/LowercaseGraphQl"
import {Profile} from "./components/Profile"
import {GqlCodeSurfer} from './components/GqlCodeSurfer'
import {GqlCodeSurferColumns} from './components/GqlCodeSurferColumns'
import {YarnCodegenInstallSnippet} from './components/snippets/YarnCodegenInstallSnippet'
import {CodegenYamlSnippet} from './components/snippets/CodegenYamlSnippet'
import {CodegenCommandSnippet} from './components/snippets/CodegenCommandSnippet'
import {DummyQuerySnippet} from './components/snippets/DummyQuerySnippet'
import {RunCodegenSnippet} from './components/snippets/RunCodegenSnippet'
import {QueryHookSnippet} from './components/snippets/QueryHookSnippet'
import {QueryErrorSnippet} from './components/snippets/QueryErrorSnippet'
import {MutationErrorSnippet} from './components/snippets/MutationErrorSnippet'
import {MutationHookSnippet} from './components/snippets/MutationHookSnippet'
import {DummyMutationSnippet} from './components/snippets/DummyMutationSnippet'
import {ConnectionQuerySnippet} from './components/snippets/ConnectionQuerySnippet'
import {ConnectionQueryWithArgumentsSnippet} from './components/snippets/ConnectionQueryWithArgumentsSnippet'
import {ApolloCacheSnippet} from './components/snippets/ApolloCacheSnippet'
import profileImageFlorian from "./images/bandfoto-florian.png"
import profileImageJan from "./images/bandfoto-jan.jpg"
import profileImageChristian from "./images/bandfoto-christian.png"
import bandfoto from "./images/bandfoto-gefaerbt3_hintergrund.jpg"
import graphPlain from "./images/graph/ohne Beschriftung.svg"
import graphLabelled from "./images/graph/mit Beschriftung.svg"
import toolGraphiQl from "./images/tooling/graphiql.png"
import toolGraphQlPlayground from "./images/tooling/gql-playground.png"
export const theme = suoraTheme;

const layoutProps = {
  theme
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <h1>{`GraphQL for React applications`}</h1>
    <hr></hr>
    <Image src={bandfoto} mdxType="Image" />
    <hr></hr>
    <Profile image={profileImageChristian} name={"Hans-Christian Otto"} email={"c.otto@suora.com"} occupation={"Consultant with Suora"} twitter={"@muhdiekuh"} textAlign="right" mdxType="Profile" />
    <hr></hr>
    <Profile image={profileImageFlorian} name={"Florian Sowade"} email={"f.sowade@suora.com"} occupation={"Consultant with Suora"} twitter={"@rioderelfte"} mdxType="Profile" />
    <hr></hr>
    <h1>{`These slides are online!`}</h1>
    <p>{`Feel free to follow along at `}<a href={`https://${getLowercaseGraphQl()}-react.suora.training/`} style={{
        color: "white"
      }}>{`https://`}<LowercaseGraphQl mdxType="LowercaseGraphQl" />{`-react.suora.training`}</a>{`.`}</p>
    <hr></hr>
    <h1>{`Our plan for today`}</h1>
    <Steps mdxType="Steps">
      <ul>
        <li parentName="ul">{`What is GraphQL?`}</li>
        <li parentName="ul">{`Fetching and manipulating data`}</li>
        <li parentName="ul">{`GraphQL with React`}</li>
        <li parentName="ul">{`Schema and typings with TypeScript`}</li>
      </ul>
    </Steps>
    <hr></hr>
    <h1>{`What's your energy level today?`}</h1>
    <div style={{
      display: "flex",
      width: "100%",
      justifyContent: "center"
    }}>
      <Image src="https://media.giphy.com/media/1j8P1JhBOiq7ZXtkqr/giphy.gif" style={{
        height: '456px',
        width: '384px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '100px'
      }} mdxType="Image" />
      <Image src="https://thumbs.gfycat.com/AdoredGlossyAmazonparrot-small.gif" style={{
        height: '456px',
        width: '384px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '100px'
      }} mdxType="Image" />
    </div>
    <hr></hr>
    <Invert mdxType="Invert">
      <h1>{`Now what's this GraphQL thing?`}</h1>
    </Invert>
    <hr></hr>
    <Invert mdxType="Invert">
      <img src={graphPlain} style={{
        height: "75vh",
        width: "auto",
        display: "flex"
      }} />
    </Invert>
    <hr></hr>
    <Invert mdxType="Invert">
      <img src={graphLabelled} style={{
        height: "75vh",
        width: "auto",
        display: "flex"
      }} />
    </Invert>
    <hr></hr>
    <h2>{`GraphQL Basics`}</h2>
    <ul>
      <Steps mdxType="Steps">
    <li>Nodes and edges</li>
    <li>Schema</li>
    <li>Client controls what data is returned via queries</li>
    <li>Decoupled from data storage</li>
      </Steps>
    </ul>
    <Notes mdxType="Notes">
      <ul>
        <li parentName="ul">{`Social media origin`}</li>
        <li parentName="ul">{`Strong typing`}</li>
        <li parentName="ul">{`Scalar and complex types`}</li>
        <li parentName="ul">{`Most self-defined types will be complex types`}</li>
      </ul>
    </Notes>
    <hr></hr>
    <GqlCodeSurferColumns themes={[suoraTheme, github]} title="A basic GraphQL schema" mdxType="GqlCodeSurferColumns">
      <Step mdxType="Step">
        <ul>
          <li parentName="ul">{`A basic node with some properties`}</li>
        </ul>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Basic schema\"",
            "title": "\"Basic",
            "schema\"": true
          }}>{`type TimeEntry {
  id: ID
  comment: String
  start: String
  end: String
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <ul>
          <li parentName="ul">{`A basic node with some properties`}</li>
          <li parentName="ul">{`Properties are nullable by default`}</li>
        </ul>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Basic schema\"",
            "title": "\"Basic",
            "schema\"": true
          }}>{`type TimeEntry {
  id: ID!
  comment: String!
  start: String!
  end: String!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <ul>
          <li parentName="ul">{`A basic node with some properties`}</li>
          <li parentName="ul">{`Nullable by default`}</li>
          <li parentName="ul">{`We can define custom scalar types`}</li>
        </ul>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Basic schema\"",
            "title": "\"Basic",
            "schema\"": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <p>{`Let's add another complex type`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Basic schema\"",
            "title": "\"Basic",
            "schema\"": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
}

type Project {
  id: ID!
  name: String!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <p>{`Node types can be used as properties in other types`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Basic schema\"",
            "title": "\"Basic",
            "schema\"": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
}
`}</code></pre>
      </Step>
    </GqlCodeSurferColumns>
    <hr></hr>
    <h1>{`Schema recap`}</h1>
    <ul>
      <li parentName="ul">{`Contract about structure of graph`}</li>
      <li parentName="ul">{`Shared between client and server`}</li>
      <li parentName="ul">{`Describes the domain model`}</li>
      <li parentName="ul">{`Describes how to query and change data from it`}</li>
    </ul>
    <hr></hr>
    <GqlCodeSurferColumns mdxType="GqlCodeSurferColumns">
      <Step mdxType="Step">
        <p>{`Node types can be used as properties in other types`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Schema with query\"",
            "title": "\"Schema",
            "with": true,
            "query\"": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <p>{`Accessing data: Let's add a Query`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Schema with query\"",
            "title": "\"Schema",
            "with": true,
            "query\"": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
}

type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <p>{`Our complete schema`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "1:18 title=\"Schema with query\"",
            "1:18": true,
            "title": "\"Schema",
            "with": true,
            "query\"": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
}

type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
      </Step>
    </GqlCodeSurferColumns>
    <hr></hr>
    <h1>{`Queries`}</h1>
    <hr></hr>
    <GqlCodeSurferColumns themes={[github, github]} mdxType="GqlCodeSurferColumns">
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat"
          }}>{`// Schema
type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json"
          }}>{``}</code></pre>
      </Step>
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Client-side operation\"",
            "title": "\"Client-side",
            "operation\"": true
          }}>{`// Schema
type Query {
  timeEntries: [TimeEntry!]!
}

// Client-side operation
query AllTimeEntries {
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json",
            "metastring": "title=\"Server response\"",
            "title": "\"Server",
            "response\"": true
          }}>{`{
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Client-side operation\"",
            "title": "\"Client-side",
            "operation\"": true
          }}>{`// Schema
type Query {
  timeEntries: [TimeEntry!]!
}

// Client-side operation
query AllTimeEntries {
  timeEntries {
    id
    comment
    start
    end
  }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json",
            "metastring": "title=\"Server response\"",
            "title": "\"Server",
            "response\"": true
          }}>{`{
  "data": {
    "timeEntries": [
      {
        "id": "1",
        "comment": "Learn React",
        "start": "2022-01-10T10:00:00.000Z",
        "end": "2022-01-10T12:00:00.000Z"
      },
      {
        "id": "2",
        "comment": "Learn Redux",
        "start": "2022-01-11T16:00:00.000Z",
        "end": "2022-01-11T18:00:00.000Z"
      }
    ]
  }
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Client-side operation\"",
            "title": "\"Client-side",
            "operation\"": true
          }}>{`// Schema
type Query {
  timeEntries: [TimeEntry!]!
}

// Client-side operation
query AllTimeEntries {
  timeEntries {
    id
    comment
    start
    end
    project {
      id
      name
    }
  }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json",
            "metastring": "title=\"Server response\"",
            "title": "\"Server",
            "response\"": true
          }}>{`{
  "data": {
    "timeEntries": [
      {
        "id": "1",
        "comment": "Learn React",
        "start": "2022-01-10T10:00:00.000Z",
        "end": "2022-01-10T12:00:00.000Z",
        "project": {
          "id": "workshop",
          "name": "Workshop"
        }
      },
      {
        "id": "2",
        "comment": "Learn Redux",
        "start": "2022-01-11T16:00:00.000Z",
        "end": "2022-01-11T18:00:00.000Z",
        "project": {
          "id": "workshop",
          "name": "Workshop"
        }
      }
    ]
  }
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "1:17 title=\"Client-side operation\"",
            "1:17": true,
            "title": "\"Client-side",
            "operation\"": true
          }}>{`// Schema
type Query {
  timeEntries: [TimeEntry!]!
}

// Client-side operation
query AllTimeEntries {
  timeEntries {
    id
    comment
    start
    end
    project {
      id
      name
    }
  }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json",
            "metastring": "1:24 title=\"Server response\"",
            "1:24": true,
            "title": "\"Server",
            "response\"": true
          }}>{`{
  "data": {
    "timeEntries": [
      {
        "id": "1",
        "comment": "Learn React",
        "start": "2022-01-10T10:00:00.000Z",
        "end": "2022-01-10T12:00:00.000Z",
        "project": {
          "id": "workshop",
          "name": "Workshop"
        }
      },
      {
        "id": "2",
        "comment": "Learn Redux",
        "start": "2022-01-11T16:00:00.000Z",
        "end": "2022-01-11T18:00:00.000Z",
        "project": {
          "id": "workshop",
          "name": "Workshop"
        }
      }
    ]
  }
}
`}</code></pre>
      </Step>
    </GqlCodeSurferColumns>
    <hr></hr>
    <h2>{`Let's try this stuff out!`}</h2>
    <Image src="https://media.giphy.com/media/rVbAzUUSUC6dO/giphy.gif" style={{
      marginBottom: '40px',
      width: '372px',
      height: '210px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }} mdxType="Image" />
    <a href="https://countries.trevorblades.com" style={{
      color: "white"
    }}>https://countries.trevorblades.com</a>
    <Steps mdxType="Steps">
      <ul>
        <li parentName="ul">{`Make yourselves familiar with the UI and syntax`}</li>
        <li parentName="ul">{`Take a look at the schema on the right`}</li>
        <li parentName="ul">{`Query Ideas`}
          <ul parentName="li">
            <li parentName="ul">{`Try to find the capitals of all African countries`}</li>
            <li parentName="ul">{`Try to query a list of country phone codes`}</li>
          </ul>
        </li>
      </ul>
    </Steps>
    <hr></hr>
    <h2>{`Time to build something ourselves! 🏗`}</h2>
    <p>{`Demo app scaffold:`}</p>
    <a href="https://bit.ly/3MB4OYs" style={{
      color: "white"
    }}>https://bit.ly/3MB4OYs</a>
    <p>{`Code Sandbox:`}</p>
    <a href="https://bit.ly/3F09q7N" style={{
      color: "white"
    }}>https://bit.ly/3F09q7N</a>
    <hr></hr>
    <h1>{`Pre-flight check 🛫`}</h1>
    <h2>{`Are you familiar with`}</h2>
    <ul>
      <li parentName="ul">{`Yarn or npm`}</li>
      <li parentName="ul">{`TypeScript`}</li>
      <li parentName="ul">{`React components and JSX`}</li>
      <li parentName="ul">{`React hooks`}</li>
    </ul>
    <hr></hr>
    <h1>{`Getting started`}</h1>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`npm install`}</inlineCode></li>
      <li parentName="ol"><inlineCode parentName="li">{`npm start`}</inlineCode></li>
      <li parentName="ol">{`Try it out`}</li>
    </ol>
    <hr></hr>
    <h1>{`Let's use Apollo and add a simple query`}</h1>
    <DummyQuerySnippet mdxType="DummyQuerySnippet" />
    <hr></hr>
    <h1>{`Executing a query using Apollo Hooks`}</h1>
    <QueryHookSnippet mdxType="QueryHookSnippet" />
    <hr></hr>
    <h1>{`Tinker time - now it's up to you 🛠💪`}</h1>
    <ul>
      <li parentName="ul">{`Implement fetching time entries from the server`}</li>
      <li parentName="ul">{`Use the provided schema and auto-generated code`}</li>
      <li parentName="ul">{`Hint: Navigate to `}<inlineCode parentName="li">{`localhost:4000`}</inlineCode>{` for a GraphQL UI to test your Queries`}</li>
    </ul>
    <hr></hr>
    <h1>{`So now we can see our time entries...`}</h1>
    <h1>{`But how do we create new ones? 🤔`}</h1>
    <hr></hr>
    <GqlCodeSurfer mdxType="GqlCodeSurfer">
      <pre><code parentName="pre" {...{
          "className": "language-gql-gatsby-compat",
          "metastring": "title=\"Extending our schema\"",
          "title": "\"Extending",
          "our": true,
          "schema\"": true
        }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-gql-gatsby-compat",
          "metastring": "title=\"Extending our schema\"",
          "title": "\"Extending",
          "our": true,
          "schema\"": true
        }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Mutation {
  addTimeEntry(comment: String!, projectId: String!, start: Date!, end: Date!): TimeEntry!
}
`}</code></pre>
    </GqlCodeSurfer>
    <hr></hr>
    <GqlCodeSurferColumns themes={[github, github]} mdxType="GqlCodeSurferColumns">
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat"
          }}>{`mutation LogTime {
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json"
          }}>{`{
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat"
          }}>{`mutation LogTime($comment: String!, $projectId: String!, $start: Date!, $end: Date!) {
  addTimeEntry(comment: $comment, projectId: $projectId, start: $start, end: $end) {
    id
    comment
    start
    end
  }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json"
          }}>{`{
  "data": {
    "addTimeEntry": {
      "id": "3",
      "comment": "Trying out some mutations",
      "start": "2022-01-11T18:00:00.000Z",
      "end": "2022-01-11T19:00:00.000Z"
    }
  }
}
`}</code></pre>
      </Step>
    </GqlCodeSurferColumns>
    <hr></hr>
    <h1>{`Mutations with Apollo`}</h1>
    <DummyMutationSnippet mdxType="DummyMutationSnippet" />
    <hr></hr>
    <h1>{`Executing mutations`}</h1>
    <MutationHookSnippet mdxType="MutationHookSnippet" />
    <hr></hr>
    <h1>{`Tinker time II - let's get to work again 🛠💪`}</h1>
    <ul>
      <li parentName="ul">{`Implement logging a new time entry on the server`}</li>
      <li parentName="ul">{`Use the provided schema and auto-generated code for mutations this time`}</li>
    </ul>
    <hr></hr>
    <h1>{`We can create and read time entries, so far so good`}</h1>
    <hr></hr>
    <h1>{`Let's look at how to do more complex operations`}</h1>
    <hr></hr>
    <GqlCodeSurferColumns mdxType="GqlCodeSurferColumns">
      <Step mdxType="Step">
        <p>{`This is our basic schema from before`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Modelling relations\"",
            "title": "\"Modelling",
            "relations\"": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
}

type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <ul>
          <li parentName="ul">{`We now add something called a `}<inlineCode parentName="li">{`Connection`}</inlineCode></li>
        </ul>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Modelling relations\" 1:32",
            "title": "\"Modelling",
            "relations\"": true,
            "1:32": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
  timeEntriesConnection: ProjectTimeEntriesConnection
}

type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <ul>
          <li parentName="ul">{`We now add something called a `}<inlineCode parentName="li">{`Connection`}</inlineCode></li>
          <li parentName="ul">{`Simply put, Connections represent a set of edges between nodes`}</li>
        </ul>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Modelling relations\" 1:32",
            "title": "\"Modelling",
            "relations\"": true,
            "1:32": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
  timeEntriesConnection: ProjectTimeEntriesConnection
}

type ProjectTimeEntriesConnection {
  edges: [ProjectTimeEntriesEdge!]!
}

type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <ul>
          <li parentName="ul">{`We now add something called a `}<inlineCode parentName="li">{`Connection`}</inlineCode></li>
          <li parentName="ul">{`Simply put, Connections represent a set of edges between nodes`}</li>
          <li parentName="ul">{`Every edge points to another node`}</li>
        </ul>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Modelling relations\" 1:32",
            "title": "\"Modelling",
            "relations\"": true,
            "1:32": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
  timeEntriesConnection: ProjectTimeEntriesConnection
}

type ProjectTimeEntriesConnection {
  edges: [ProjectTimeEntriesEdge!]!
}

type ProjectTimeEntriesEdge {
  node: TimeEntry!
}

type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <ul>
          <li parentName="ul">{`We now add something called a `}<inlineCode parentName="li">{`Connection`}</inlineCode></li>
          <li parentName="ul">{`Simply put, Connections represent a set of edges between nodes`}</li>
          <li parentName="ul">{`Every edge points to another node`}</li>
          <li parentName="ul">{`Edges can have properties themselves`}</li>
        </ul>
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Modelling relations\" 1:32",
            "title": "\"Modelling",
            "relations\"": true,
            "1:32": true
          }}>{`scalar Date

type TimeEntry {
  id: ID!
  comment: String!
  start: Date!
  end: Date!
  project: Project!
}

type Project {
  id: ID!
  name: String!
  timeEntriesConnection: ProjectTimeEntriesConnection
}

type ProjectTimeEntriesConnection {
  edges: [ProjectTimeEntriesEdge!]!
}

type ProjectTimeEntriesEdge {
  node: TimeEntry!
  authoredAt: Date!
}

type Query {
  timeEntries: [TimeEntry!]!
}
`}</code></pre>
      </Step>
    </GqlCodeSurferColumns>
    <hr></hr>
    <h1>{`A Query using a Connection`}</h1>
    <ConnectionQuerySnippet mdxType="ConnectionQuerySnippet" />
    <hr></hr>
    <GqlCodeSurferColumns mdxType="GqlCodeSurferColumns">
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Query\"",
            "title": "\"Query\""
          }}>{`query AllProjectsWithTimeEntries {
  projects {
    id
    name
  }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json",
            "metastring": "title=\"Result\"",
            "title": "\"Result\""
          }}>{`{
  "data": {
    "projects": [
      {
        "id": "workshop",
        "name": "Workshop"
      }
    ]
  }
}
`}</code></pre>
      </Step>
      <Step mdxType="Step">
        <pre><code parentName="pre" {...{
            "className": "language-gql-gatsby-compat",
            "metastring": "title=\"Query\"",
            "title": "\"Query\""
          }}>{`query AllProjectsWithTimeEntries {
  projects {
    id
    name
    timeEntriesConnection {
      edges {
        node {
          id
          comment
        }
      }
    }
  }
}
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-json",
            "metastring": "title=\"Result\"",
            "title": "\"Result\""
          }}>{`{
  "data": {
    "projects": [
      {
        "id": "workshop",
        "name": "Workshop",
        "timeEntriesConnection": {
          "edges": [
            {
              "node": {
                "id": "1",
                "comment": "Learn React"
              }
            },
            {
              "node": {
                "id": "2",
                "comment": "Learn Redux"
              }
            }
          ]
        }
      }
    ]
  }
}
`}</code></pre>
      </Step>
    </GqlCodeSurferColumns>
    <hr></hr>
    <h1>{`Query arguments`}</h1>
    <ConnectionQueryWithArgumentsSnippet mdxType="ConnectionQueryWithArgumentsSnippet" />
    <h3>{`(We already snuck those in with mutations before - so sneaky 🥷🥷)`}</h3>
    <hr></hr>
    <h2>{`Let's try this stuff out some more!`}</h2>
    <Image src="https://media.giphy.com/media/rVbAzUUSUC6dO/giphy.gif" style={{
      marginBottom: '40px',
      width: '372px',
      height: '210px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }} mdxType="Image" />
    <a href={`https://${getLowercaseGraphQl()}.org/swapi-${getLowercaseGraphQl()}`} style={{
      color: "white"
    }}>https://<LowercaseGraphQl mdxType="LowercaseGraphQl" />.org/swapi-<LowercaseGraphQl mdxType="LowercaseGraphQl" /></a>
    <Steps mdxType="Steps">
      <ul>
        <li parentName="ul">{`Familiarize yourselves with connections and arguments`}</li>
        <li parentName="ul">{`Pagination is done using a combination of both`}</li>
        <li parentName="ul">{`Query inspirations`}
          <ul parentName="li">
            <li parentName="ul">{`Find the first three films`}</li>
            <li parentName="ul">{`Find all character names from the first film`}</li>
            <li parentName="ul">{`Find the Millenium Falcon's pilots (may need multiple queries)`}</li>
          </ul>
        </li>
      </ul>
    </Steps>
    <hr></hr>
    <h1>{`Tinker time III 🛠💪`}</h1>
    <Steps mdxType="Steps">
      <ul>
        <li parentName="ul">{`List all projects and their linked time entries`}</li>
        <li parentName="ul">{`Use what you just learned about Connections`}</li>
        <li parentName="ul">{`Stretch goal: Make the list of projects searchable`}</li>
        <li parentName="ul">{`Remember query arguments`}</li>
      </ul>
    </Steps>
    <hr></hr>
    <h1>{`We've got the basics covered - questions so far?`}</h1>
    <hr></hr>
    <h1>{`🚧🚧🚧 So what about errors? 🚧🚧🚧`}</h1>
    <hr></hr>
    <h1>{`Handling errors in queries`}</h1>
    <QueryErrorSnippet mdxType="QueryErrorSnippet" />
    <hr></hr>
    <h1>{`Handling errors in mutations`}</h1>
    <MutationErrorSnippet mdxType="MutationErrorSnippet" />
    <hr></hr>
    <h1>{`Tooling`}</h1>
    <div style={{
      display: "flex",
      width: "100%",
      height: "50%",
      justifyContent: "center"
    }}>
      <Image src={toolGraphiQl} style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: "50px",
        width: "800px",
        backgroundPosition: "0% 0%"
      }} mdxType="Image" />
      <Image src={toolGraphQlPlayground} style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: "50px",
        width: "800px",
        backgroundPosition: "0% 0%"
      }} mdxType="Image" />
    </div>
    <hr></hr>
    <h1>{`Apollo Cache`}</h1>
    <ApolloCacheSnippet mdxType="ApolloCacheSnippet" />
    <ul>
      <li parentName="ul">{`Apollo comes with an `}<inlineCode parentName="li">{`InMemoryCache`}</inlineCode>{` out of the box`}</li>
      <li parentName="ul">{`Other options can be implemented`}</li>
      <li parentName="ul">{`Apollo uses the cache for "smart" updates`}</li>
      <li parentName="ul">{`How the cache is used can be controlled with fetch policies`}</li>
      <li parentName="ul">{`Local state of data can be used for "optimistic updates"`}</li>
    </ul>
    <hr></hr>
    <h1>{`When to use GraphQL vs. REST`}</h1>
    <p>{`As always: It depends`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Build your own queries`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Fetch only what you need`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Strongly typed and documented`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Can avoid waterfall effect`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Things like file uploads, streaming and batch mutations can be tricky`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Request caching is harder`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Harder to optimize server-side`}</p>
      </li>
    </ul>
    <hr></hr>
    <h1>{`Subscriptions`}</h1>
    <hr></hr>
    <h1>{`Schema changes and TypeScript`}</h1>
    <hr></hr>
    <h1>{`Testing`}</h1>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;